import { watch, onBeforeUnmount } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useInboxStore } from '../stores/inboxStore';
import type { InboxNotification } from '../api/notificationApi';
import { isInboxFeatureEnabled } from '../config/inboxFeature';
import { useSubscriptionStatus } from '@/shared/composables/useSubscriptionStatus';
import { useTermsConsent } from '@/shared/composables/useTermsConsent';

let sharedEventSource: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

export function useNotificationStream() {
  if (!isInboxFeatureEnabled()) return;

  const sessionStore = useSessionStore();
  const inboxStore = useInboxStore();
  // 구독 만료·약관 미동의로 하드 차단된 상태에서는 /api/notifications/*가 항상 403이라
  // 재연결을 계속 시도해봐야 의미 없다 — 차단 해제 전까지는 아예 연결을 시도하지 않는다.
  const { accessBlocked } = useSubscriptionStatus();
  const { needsConsent } = useTermsConsent();
  const canConnect = () => sessionStore.isAuthenticated && !accessBlocked.value && !needsConsent.value;

  const disconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (sharedEventSource) {
      sharedEventSource.close();
      sharedEventSource = null;
    }
  };

  const connect = () => {
    if (sharedEventSource || !canConnect()) return;

    const url = '/api/notifications/stream';
    sharedEventSource = new EventSource(url, { withCredentials: true });

    sharedEventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'ping' || data.type === 'connection') return;

        if (typeof data.unreadCount === 'number') {
          inboxStore.setUnreadCount(data.unreadCount);
        }

        if (data.type === 'notification:new' && data.notification) {
          const n = data.notification as InboxNotification;
          inboxStore.prependNotification(n);
          if (n.notificationId) {
            inboxStore.markNewArrival(n.notificationId);
          }
          if (typeof data.unreadCount === 'number') {
            inboxStore.setUnreadCount(data.unreadCount);
          } else {
            inboxStore.refreshUnreadCount();
          }
        }
      } catch {
        /* ignore malformed SSE */
      }
    };

    sharedEventSource.onerror = () => {
      disconnect();
      if (canConnect()) {
        reconnectTimer = setTimeout(connect, 5000);
      }
    };
  };

  watch(
    () => [sessionStore.isAuthenticated, accessBlocked.value, needsConsent.value] as const,
    ([authenticated, blocked, consentNeeded]) => {
      if (authenticated && !blocked && !consentNeeded) {
        inboxStore.refreshUnreadCount();
        connect();
      } else {
        disconnect();
        if (!authenticated) inboxStore.reset();
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    disconnect();
  });
}
